import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Banknote, Plus, Wallet } from 'lucide-react';
import { Button, FormField, KPICard, Modal, PageHeader, Skeleton, Tabs } from '@/components/ui';
import { CaissesAvance } from '@/modules/tresorerie/CaissesAvance';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type TypeCompteTresorerie,
  createCompteTresorerie,
  createMouvement,
  getTresorerieDashboard,
  listComptesTresorerie,
} from '@/services/finance';
import { toast } from '@/store/toastStore';
import { formatDZD, formatDZDCompact } from '@/utils/algeria';

export function TresoreriePage() {
  const queryClient = useQueryClient();
  const [vue, setVue] = useState('treso');
  const [compteOpen, setCompteOpen] = useState(false);
  const [mvtOpen, setMvtOpen] = useState(false);
  const { data: comptes, isLoading } = useQuery({ queryKey: ['treso-comptes'], queryFn: listComptesTresorerie });
  const { data: dash } = useQuery({ queryKey: ['treso-dashboard'], queryFn: getTresorerieDashboard });

  const compteForm = useForm<{ libelle: string; type: TypeCompteTresorerie; banque: string; solde_initial: number }>({ defaultValues: { type: 'BANQUE' } });
  const mvtForm = useForm<{ compte_id: string; date_mouvement: string; sens: 'ENTREE' | 'SORTIE'; montant: number; libelle: string }>({ defaultValues: { sens: 'ENTREE', date_mouvement: new Date().toISOString().slice(0, 10) } });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['treso-comptes'] });
    queryClient.invalidateQueries({ queryKey: ['treso-dashboard'] });
  };

  const compteMutation = useMutation({
    mutationFn: (v: { libelle: string; type: TypeCompteTresorerie; banque: string; solde_initial: number }) =>
      createCompteTresorerie({ ...v, solde_initial: Number(v.solde_initial) || 0 }),
    onSuccess: () => { invalidate(); setCompteOpen(false); compteForm.reset(); toast.success('Compte créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const mvtMutation = useMutation({
    mutationFn: (v: { compte_id: string; date_mouvement: string; sens: 'ENTREE' | 'SORTIE'; montant: number; libelle: string }) =>
      createMouvement({ ...v, montant: Number(v.montant) }),
    onSuccess: () => { invalidate(); setMvtOpen(false); mvtForm.reset({ sens: 'ENTREE', date_mouvement: new Date().toISOString().slice(0, 10) }); toast.success('Mouvement enregistré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <>
      <PageHeader
        title="Trésorerie"
        subtitle="Soldes, encaissements et décaissements"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Trésorerie' }]}
        actions={vue === 'treso' ? (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setCompteOpen(true)}><Plus /> Compte</Button>
            <Button onClick={() => setMvtOpen(true)}><Plus /> Mouvement</Button>
          </div>
        ) : undefined}
      />

      <Tabs className="mb-4" value={vue} onChange={setVue} tabs={[{ key: 'treso', label: 'Comptes & mouvements' }, { key: 'caisses', label: "Caisses d'avance" }]} />

      {vue === 'caisses' ? <CaissesAvance /> : (<>
      <div className="mb-6 rounded-lg border border-border bg-surface-secondary p-3 text-sm text-content-secondary shadow-sm">
        ⓘ Ce module est <span className="font-semibold text-content-primary">alimenté automatiquement</span> : chaque
        encaissement client et règlement fournisseur crée un mouvement (entrée / sortie) sur le compte du mode de
        paiement (espèces → caisse, sinon banque). Vous pouvez aussi saisir des mouvements manuels (rapprochement,
        virements internes). Le tableau de bord, lui, affiche la trésorerie <span className="font-semibold text-content-primary">comptable</span>.
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KPICard title="Solde total" value={formatDZDCompact(dash?.solde_total ?? 0)} icon={Wallet} accent="#1a1a2e" />
        <KPICard title="Entrées (mois)" value={formatDZDCompact(dash?.entrees_mois ?? 0)} icon={ArrowDownLeft} accent="#00C875" />
        <KPICard title="Sorties (mois)" value={formatDZDCompact(dash?.sorties_mois ?? 0)} icon={ArrowUpRight} accent="#E2445C" />
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {comptes?.map((c) => (
            <div key={c.id} className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex size-9 items-center justify-center rounded-md bg-brand-primary/10 text-brand-primary"><Banknote className="size-5" /></span>
                <span className="text-xs font-semibold uppercase tracking-wide text-content-muted">{c.type}</span>
              </div>
              <p className="text-sm font-semibold text-content-primary">{c.libelle}</p>
              {c.banque && <p className="text-xs text-content-muted">{c.banque}</p>}
              <p className="mt-2 text-xl font-bold tabular-nums text-content-primary">{formatDZD(c.solde_actuel)}</p>
            </div>
          ))}
          {comptes && comptes.length === 0 && <p className="text-sm text-content-muted">Aucun compte de trésorerie.</p>}
        </div>
      )}
      </>)}

      <Modal open={compteOpen} onOpenChange={setCompteOpen} title="Nouveau compte de trésorerie"
        footer={<><Button variant="secondary" onClick={() => setCompteOpen(false)}>Annuler</Button><Button onClick={compteForm.handleSubmit((v) => compteMutation.mutate(v))} loading={compteMutation.isPending}>Créer</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Libellé" required className="sm:col-span-2"><Input {...compteForm.register('libelle')} /></FormField>
          <FormField label="Type">
            <select {...compteForm.register('type')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="BANQUE">Banque</option><option value="CCP">CCP</option><option value="CAISSE">Caisse</option>
            </select>
          </FormField>
          <FormField label="Banque"><Input {...compteForm.register('banque')} /></FormField>
          <FormField label="Solde initial (DZD)"><Input type="number" step="1" {...compteForm.register('solde_initial')} /></FormField>
        </div>
      </Modal>

      <Modal open={mvtOpen} onOpenChange={setMvtOpen} title="Nouveau mouvement"
        footer={<><Button variant="secondary" onClick={() => setMvtOpen(false)}>Annuler</Button><Button onClick={mvtForm.handleSubmit((v) => mvtMutation.mutate(v))} loading={mvtMutation.isPending}>Enregistrer</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Compte" required className="sm:col-span-2">
            <select {...mvtForm.register('compte_id')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="">— Sélectionner —</option>
              {comptes?.map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
            </select>
          </FormField>
          <FormField label="Sens">
            <select {...mvtForm.register('sens')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              <option value="ENTREE">Entrée</option><option value="SORTIE">Sortie</option>
            </select>
          </FormField>
          <FormField label="Montant (DZD)" required><Input type="number" step="1" {...mvtForm.register('montant')} /></FormField>
          <FormField label="Date"><Input type="date" {...mvtForm.register('date_mouvement')} /></FormField>
          <FormField label="Libellé" required><Input {...mvtForm.register('libelle')} /></FormField>
        </div>
      </Modal>
    </>
  );
}
